import React, { useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as PreviousIcon } from "assets/icons/image-view-chevron-left.svg";
import { ReactComponent as NextIcon } from "assets/icons/image-view-chevron-right.svg";
import { ReactComponent as CloseIcon } from "assets/icons/white-close-icon.svg";
import { ReactComponent as DownloadIcon } from "assets/icons/white-download-icon.svg";
import { ReactComponent as ZoomInIcon } from "assets/icons/zoom-in.svg";
import { ReactComponent as ZoomOutIcon } from "assets/icons/zoom-out.svg";
import { PhotoSlider } from "react-photo-view";
import { DataType } from "react-photo-view/dist/types";
import { capitalizeWords } from "utils";
import { INDEX_OFFSET, START_INDEX } from "utils/constants";

import { customWarningToast } from "ui/custom-toast";
import { Heading2, Paragraph1, Paragraph3 } from "ui/typography";

import { IImageViewerProps, TImageViewerZoomType } from "./types";

const ZOOM_SCALE = 0.5;
const MIN_SCALE_VALUE = 0.5;
const MAX_SCALE_VALUE = 6.5;
const IMAGE_INDEX_UPDATE_VALUE = 1;
const DEFAULT_IMAGE_SCALE = 1;
export const FALL_BACK_IMAGE_URL =
  "https://placehold.co/150?text=Failed+To+Load&font=poppins";

export const ImageViewer = (props: IImageViewerProps) => {
  const {
    imageDataList,
    isVisible,
    imageIndex,
    handleImageIndexChange,
    handleCloseImageViewer,
    uploaderName,
  } = props;
  const [imageScale, setImageScale] = useState(DEFAULT_IMAGE_SCALE);
  const [imageIndexUsed, setImageIndexUsed] = useState(START_INDEX);

  const imageUrls: string[] = imageDataList.map(({ imageUrl }) => imageUrl);

  const handleZoomImage = (
    scale: number,
    onScale: (scale: number) => void,
    zoomType: TImageViewerZoomType
  ) => {
    const newScaleValue =
      zoomType === "out" ? scale - ZOOM_SCALE : scale + ZOOM_SCALE;

    if (newScaleValue === MIN_SCALE_VALUE) {
      customWarningToast({
        title: "Image zoom",
        message: "You cannot zoom out any more",
      });
      onScale(MIN_SCALE_VALUE);
      setImageScale(MIN_SCALE_VALUE);
      return;
    }

    if (newScaleValue === MAX_SCALE_VALUE) {
      customWarningToast({
        title: "Image zoom",
        message: "You cannot zoom in any more",
      });
      onScale(MAX_SCALE_VALUE);
      setImageScale(MAX_SCALE_VALUE);
      return;
    }

    onScale(newScaleValue);
    setImageScale(newScaleValue);
  };

  const handleDownloadImage = (imageData: DataType) => {
    const anchorElement = document.createElement("a");
    anchorElement.href = imageData.key as string;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
  };

  useEffect(() => {
    if (!isVisible) {
      setImageScale(DEFAULT_IMAGE_SCALE);
    }
  }, [isVisible]);

  useEffect(() => {
    setImageIndexUsed(imageIndex);
  }, [imageIndex]);

  return (
    <PhotoSlider
      className="image-viewer-wrapper"
      maskOpacity={0.9}
      toolbarRender={({ scale, onScale, images, index, onClose }) => {
        return (
          <>
            <Box className="flex flex-col gap-4 w-full">
              <Box
                className="flex items-center justify-between"
                css={{
                  "@media screen and (max-width: 768px)": {
                    flexDirection: "column-reverse",
                    alignItems: "flex-start",
                    gap: "12px",
                  },
                }}
              >
                <Heading2
                  textColor="#ffffff"
                  lineHeight="28.8px"
                >
                  {imageDataList[index].title}
                </Heading2>
                <Box
                  className="flex items-center gap-12 w-full max-w-[252px]"
                  css={{
                    "& svg": {
                      width: "24px",
                      height: "24px",
                      flexShrink: "0",
                    },

                    "@media screen and (max-width: 768px)": {
                      alignSelf: "flex-end",
                    },
                  }}
                >
                  <Box className="flex gap-6">
                    <ZoomInIcon
                      className="cursor-pointer"
                      onClick={() => handleZoomImage(scale, onScale, "in")}
                    />
                    <ZoomOutIcon
                      className="cursor-pointer"
                      onClick={() => handleZoomImage(scale, onScale, "out")}
                    />
                    <DownloadIcon
                      className="cursor-pointer"
                      onClick={() => handleDownloadImage(images[index])}
                    />
                  </Box>
                  <Box
                    className="flex cursor-pointer items-center gap-2 w-[84px]"
                    onClick={onClose}
                  >
                    <CloseIcon />
                    <Paragraph1
                      fontSize="18.5px"
                      lineHeight="24px"
                      textColor="#ffffff"
                      fontWeight="700"
                    >
                      Close
                    </Paragraph1>
                  </Box>
                </Box>
              </Box>
              <Box className="flex items-center gap-[6px]">
                <Box className="w-8 h-8 rounded-[50%] flex justify-center items-center text-center bg-[#009642] text-white font-medium text-[12.8px] leading-[19.2px]">
                  {uploaderName.charAt(START_INDEX).toUpperCase()}
                </Box>
                <Paragraph3
                  fontWeight="500"
                  textColor="#ffffff"
                >
                  Uploaded by {capitalizeWords(uploaderName)}
                </Paragraph3>
              </Box>
            </Box>
            <Box
              css={{
                position: "fixed",
                top: "50%",
                transform: "translateY(-50%)",
                cursor:
                  index === START_INDEX ||
                  imageUrls[index - INDEX_OFFSET].endsWith(".pdf")
                    ? "not-allowed"
                    : "pointer",
              }}
              onClick={() => {
                if (
                  index === START_INDEX ||
                  imageUrls[index - INDEX_OFFSET].endsWith(".pdf")
                )
                  return;
                if (handleImageIndexChange) {
                  handleImageIndexChange(index - IMAGE_INDEX_UPDATE_VALUE);
                }
              }}
            >
              <PreviousIcon />
            </Box>
            <Box
              css={{
                position: "fixed",
                top: "50%",
                transform: "translateY(-50%)",
                cursor:
                  index === imageUrls.length - INDEX_OFFSET ||
                  imageUrls[index + INDEX_OFFSET].endsWith(".pdf")
                    ? "not-allowed"
                    : "pointer",
                right: "40px",
              }}
              onClick={() => {
                if (
                  index === imageUrls.length - INDEX_OFFSET ||
                  imageUrls[index + INDEX_OFFSET].endsWith(".pdf")
                )
                  return;
                if (handleImageIndexChange) {
                  handleImageIndexChange(index + IMAGE_INDEX_UPDATE_VALUE);
                }
              }}
            >
              <NextIcon />
            </Box>
          </>
        );
      }}
      images={imageUrls.map((item) => ({
        key: item,
        render: () => {
          return (
            <Box
              css={{
                width: "100%",
                maxWidth: "30%",
                margin: "auto",
                transform: `scale(${imageScale})`,
              }}
            >
              <img src={item} />
            </Box>
          );
        },
      }))}
      visible={isVisible}
      onClose={handleCloseImageViewer}
      index={imageIndexUsed}
      onIndexChange={handleImageIndexChange}
      maskClosable={false}
    />
  );
};
